import SubscriptionCTA from "@/components/Modals/SubscriptionCTA";
import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { LessonI } from "@/hooks/types";
import extractVideoId from "@/scripts/extractVideoId";
import { getHostname } from "@/scripts/getHostname";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { FileText, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import { NavLink as Link } from "react-router-dom";
import { storage } from "../../../firebaseConfig";
import { endpoints } from "../../../hooks/endpoints";
import {
  convertText,
  getAllowedCategoriesForPermission,
  getAncestors,
  getFileUrl,
  getToken,
  isSubscribed,
} from "../../../hooks/utils";
import { payment_methods } from "../../../scripts/payment-methods";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { pushModal } from "../../../store/slices/modalsSlice";
import {
  invokePortalRefetch,
  invokeUserRefetch,
} from "../../../store/slices/storeSlice";
import Animation from "../../Animation";
import GeneralMessageModal from "../../Modals/GeneralMessageModal";
import Separator from "../../Separator";
import DynamicCategorySelector from "../DynamicCategorySelector";
import FileUploadProgressBar from "../FileUploadProgressBar";
import { LessonType } from "./CreateLessonWrapper";

const lessonInitialData = {
  lesson_title: "",
  video_on_off: "yes",
  prod_grade: 0,
  prod_cat: 0,
  price: 0,
  allowed_views: 0,
  allowed_time: 0,
  last_purchase_date: "",
  prereq_type: "",
  prereq: "",
  xvast_protection: false,
  payment_method_code: true,
  payment_method_fawry: false,
  hide_lesson_price: false,
  quiz_required: false,
  quiz_id: "",
  hw_id: "",
  comments_or_hw: "",
  lessonFiles: [],
  videos: [],
};

const CreateLesson = ({
  setSessionType,
}: {
  setSessionType: (x: LessonType | null) => void;
}) => {
  const [lesson, setLesson] = useState<LessonI>(lessonInitialData as LessonI);

  const [fileProgress, setFileProgress] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const dispatch = useAppDispatch();

  const { userProfileData, portal } = useAppSelector((state) => state.store);

  const allowedCats = getAllowedCategoriesForPermission("can_publish_lessons");

  const allCats = allowedCats
    ? portal?.cats?.filter((cat) => allowedCats.includes(cat.term_id))
    : portal?.cats || [];

  const handleFileUploadFb = (e) => {
    if (!e.target.files[0]) {
      dispatch(
        pushModal(<GeneralMessageModal msg="يجب اختيار صورة" btnMsg="حسنا" />)
      );
      return;
    }

    if (e.target.files[0].size > 20000000) {
      dispatch(
        pushModal(
          <GeneralMessageModal
            msg="حجم الملف يجب ان يكون اقل من 20 ميغابايت"
            btnMsg="حسنا"
          />
        )
      );
      return;
    }

    let fileInfo = {
      name: e.target.files[0].name,
      size: e.target.files[0].size,
      type: e.target.files[0].type,
      url: "",
    };

    const imageRef = ref(
      storage,
      `files-${new Date().getTime()}-${e.target.files[0].name.replaceAll(
        "-",
        "."
      )}`
    );
    const uploadTask = uploadBytesResumable(imageRef, e.target.files[0]);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setFileProgress(progress.toFixed(2));
        console.log("Upload is " + progress + "% done");
      },
      (error) => {
        console.log(error);
        dispatch(
          pushModal(<GeneralMessageModal msg="حدث خطأ" btnMsg="حسنا" />)
        );
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setLesson((prev) => ({
            ...prev,
            lessonFiles: [
              ...prev.lessonFiles,
              { ...fileInfo, url: getFileUrl(downloadURL) },
            ],
          }));
          // setLessonFiles((prev) => [...prev, getFileUrl(downloadURL)]);
          setFileProgress(null);
        });
      }
    );
  };

  const getVideoInfo = (url: string) => {
    if (!url) return { host: "", id: "" };
    try {
      const splitted = url.split("?");
      const filtered =
        splitted.length > 2 ? splitted.slice(0, 2).join("?") : url;
      const parsed = new URL(filtered);
      // YouTube short link
      if (parsed.hostname === "youtu.be") {
        return {
          host: "youtube",
          id: parsed.pathname.replace("/", ""),
          href: parsed.origin + parsed.pathname,
        };
      }
      // YouTube long link
      if (parsed.hostname.includes("youtube.com")) {
        const id = parsed.searchParams.get("v");
        return {
          host: "youtube",
          id: id || "",
          href: parsed.origin + parsed.pathname + "?v=" + id,
        };
      }
      // Vimeo
      if (parsed.hostname.includes("vimeo.com")) {
        return {
          host: "vimeo",
          id: parsed.pathname.replace("/", ""),
          href: url,
        };
      }
      // MediaDelivery (fallback)
      if (parsed.hostname.includes("iframe.mediadelivery.net")) {
        return { host: "mediadelivery", id: url, href: url };
      }
      // Unknown
      return { host: "unknown", id: url, href: url };
    } catch {
      // Not a valid URL
      return { host: "", id: url, href: url };
    }
  };

  const handleSubmitLesson = async (e) => {
    e.preventDefault();

    setPublishing(true);

    try {
      const response = await fetch(endpoints.createLesson, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        body: JSON.stringify(lesson),
      });

      if (!response.ok) {
        // Handle server errors or other issues
        console.error("Error submitting the form");
        return;
      } else {
        const successInfo = await response.json();
        setPublishing(false);
        setSuccessMsg(successInfo);
        dispatch(invokePortalRefetch());
        dispatch(invokeUserRefetch());
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Animation>
      <>
        <h3 className="bg-primary mb-6 p-3 text-white rounded-xl shadow-md font-bold flex items-center">
          <p className="flex-1 p-0 m-0">نشر حصة مسجلة جديدة</p>
          <button className="m-0 p-0" onClick={() => setSessionType(null)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              fill="white"
              className="bi bi-arrow-left-circle-fill"
              viewBox="0 0 16 16"
            >
              <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0m3.5 7.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5z" />
            </svg>
          </button>
        </h3>
        <form
          onSubmit={(e) => handleSubmitLesson(e)}
          className="space-y-4 p-4 bg-white rounded-xl shadow-sm"
        >
          <h3 className="text-xl font-bold border-b-2 border-(--primary-color-light) pb-2 w-fit">
            البيانات الأساسية
          </h3>
          <div className="flex flex-col gap-2">
            <label htmlFor="lesson_title" className="text-base w-full">
              إسم الحصة
            </label>
            <input
              type="text"
              className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
              id="lesson_title"
              name="lesson_title"
              placeholder=""
              required
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  lesson_title: e.target.value,
                }))
              }
              value={lesson.lesson_title}
            />
          </div>

          <select
            name="video_on_off"
            onChange={(e) =>
              setLesson((prev) => ({
                ...prev,
                video_on_off: e.target.value as "yes" | "no",
              }))
            }
            value={lesson.video_on_off}
            hidden
            className="w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light)"
          >
            <option value="" disabled>
              نوع الحصة
            </option>
            <option value="yes">فيديو</option>
            <option value="no">إمتحان</option>
          </select>

          <DynamicCategorySelector
            filteredCats={allCats}
            excludeCatsThatHasLessons={false}
            categoryUpdateHanlderFunction={(value) => {
              setLesson((prev) => ({
                ...prev,
                prod_cat: Number(value),
              }));

              setLesson((prev) => ({
                ...prev,
                prod_grade: Math.max(
                  ...getAncestors(portal.cats, Number(value)).map(
                    (cat) => cat.term_id
                  )
                ),
              }));
            }}
          />

          {/* <div className="flex flex-col gap-2">
            <label htmlFor="prod_grade">المرحلة الدراسية</label>
            <select
              value={lesson.prod_grade}
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  prod_grade: Number(e.target.value),
                }))
              }
              id="prod_grade"
              name="prod_grade"
              className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light)"
              required
            >
              <option value={0} disabled></option>
              {cats
                .filter((item) => item.parent === 0)
                ?.map((cat) => (
                  <option value={cat.term_id} key={cat.term_id}>
                    {cat.name.indexOf("|") === -1
                      ? cat.name
                      : cat.name.split("|")[0]}
                  </option>
                ))}
            </select>
          </div> */}

          {/* {!!lesson.prod_grade && (
            <div className="flex flex-col gap-2">
              <label htmlFor="prod_cat">المجموعة</label>
              <select
                // ref={lessonCat}
                onChange={(e) =>
                  setLesson((prev) => ({
                    ...prev,
                    prod_cat: Number(e.target.value),
                  }))
                }
                value={lesson.prod_cat}
                id="prod_cat"
                name="prod_cat"
                className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light)"
                aria-label="المجموعة"
                required
              >
                <option selected></option>
                {cats
                  .filter((item) => item.parent === lesson.prod_grade)
                  ?.map((cat) => (
                    <option
                      value={cat.term_taxonomy_id}
                      key={cat.term_taxonomy_id}
                    >
                      {cat.name.indexOf("|") === -1
                        ? cat.name
                        : cat.name.split("|")[0]}
                    </option>
                  ))}
              </select>
            </div>
          )} */}

          <div className="flex flex-col gap-2">
            <label htmlFor="price" className="text-base">
              السعر
            </label>
            <input
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  price: Number(e.target.value),
                }))
              }
              value={lesson.price}
              type="number"
              className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
              id="price"
              name="price"
              min={1}
              placeholder=" "
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="allowed_views" className="text-base">
              عدد المشاهدات المتاحة لكل طالب (لو مفتوح اكتب 0)
            </label>
            <input
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  allowed_views: Number(e.target.value),
                }))
              }
              value={lesson.allowed_views}
              type="number"
              className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
              id="allowed_views"
              name="allowed_views"
              placeholder=" "
              min={0}
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="allowed_time" className="text-base">
              عدد أيام صلاحية الحصة بعد الشراء (لو مفتوح اكتب 0)
            </label>
            <input
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  allowed_time: Number(e.target.value),
                }))
              }
              value={lesson.allowed_time}
              type="number"
              className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
              id="allowed_time"
              name="allowed_time"
              placeholder=" "
              min={0}
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="last_purchase_date" className="text-base">
              اخر ميعاد لشراء الحصة (إختياري)
            </label>
            <input
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  last_purchase_date: e.target.value,
                }))
              }
              value={lesson.last_purchase_date}
              type="datetime-local"
              className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
              id="last_purchase_date"
              name="last_purchase_date"
              min={1}
              placeholder=" "
            />
          </div>
          <p className="my-2">
            يجب على الطالب حل واجب أو كويز حصة معينة قبل شراء هذه الحصة
          </p>
          <select
            onChange={(e) =>
              setLesson((prev) => ({
                ...prev,
                prereq_type: e.target.value as "quiz" | "hw",
              }))
            }
            value={lesson.prereq_type}
            name="prereq_type"
            className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) mb-3"
            aria-label="يجب على الطالب حل واجب أو كويز حصة معينة قبل شراء هذه الحصة"
            disabled={!isSubscribed()}
          >
            <option value="" selected>
              إختر
            </option>
            <option value="quiz">نعم يجب حل الكويز</option>
            <option value="hw">نعم يجب حل الواجب</option>
          </select>
          {lesson.prereq_type && (
            <select
              name="prereq"
              className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) mb-3"
              aria-label="choose-prereq"
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  prereq: e.target.value,
                }))
              }
              value={lesson.prereq}
            >
              <option disabled selected>
                إختر الحصة
              </option>
              {userProfileData?.hws_quizzes_lessons
                ?.filter((item) => item.post_type === "product")
                ?.map((lesson) => (
                  <option value={lesson.ID} key={lesson.ID}>
                    {convertText(lesson.post_title)}
                  </option>
                ))}
            </select>
          )}
          {!!portal?.allowed_payment_methods && (
            <div className="my-6">
              <p className="font-bold my-2">طرق الدفع</p>
              <div className="flex flex-col sm:flex-row sm:gap-4 gap-2">
                {portal?.allowed_payment_methods?.map((method) => (
                  <div className=" flex items-center" key={method}>
                    <input
                      className="size-4 text-primary focus:ring-(--primary-color-light) border-gray-300 rounded"
                      type="checkbox"
                      id={`payment_method_${method}`}
                      name={`payment_method_${method}`}
                      onChange={(e) =>
                        setLesson((prev) => ({
                          ...prev,
                          [`payment_method_${method}`]: e.target.checked,
                        }))
                      }
                      checked={lesson[`payment_method_${method}`]}
                    />
                    <label
                      className="mr-2"
                      htmlFor={`payment_method_${method}`}
                    >
                      {payment_methods[method]}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="my-3 flex items-center">
            <input
              className="size-4 text-primary focus:ring-(--primary-color-light) border-gray-300 rounded"
              type="checkbox"
              id="hide_lesson_price"
              name="hide_lesson_price"
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  hide_lesson_price: e.target.checked,
                }))
              }
              checked={lesson.hide_lesson_price}
            />
            <label className="mr-2" htmlFor="hide_lesson_price">
              أخفاء سعر الحصة
            </label>
          </div>
          <div className="my-3 hidden items-center">
            <input
              className="size-4 text-primary focus:ring-(--primary-color-light) border-gray-300 rounded"
              type="checkbox"
              id="xvast_protection"
              name="xvast_protection"
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  xvast_protection: e.target.checked,
                }))
              }
              // checked={lesson.xvast_protection}
              checked={false}
              hidden
            />
            <label className="mr-2" htmlFor="xvast_protection">
              تفعيل الحماية (الطالب لازم ينزل البرنامج عشان يشوف الحصة)
            </label>
          </div>
          <div className="my-3 flex items-center">
            <input
              onChange={(e) =>
                setLesson((prev) => ({
                  ...prev,
                  quiz_required: e.target.checked,
                }))
              }
              checked={lesson.quiz_required}
              className="size-4 text-primary focus:ring-(--primary-color-light) border-gray-300 rounded"
              type="checkbox"
              id="quiz_required"
              name="quiz_required"
              disabled={!isSubscribed()}
            />
            <label className="mr-2" htmlFor="quiz_required">
              يجب دخول الكويز قبل مشاهدة الحصة
            </label>
          </div>
          <Separator />
          <h3 className="text-xl font-bold border-b-2 border-(--primary-color-light) pb-2 w-fit">
            الإمتحان و الواجب
          </h3>
          <div className="space-y-3">
            <div className="flex flex-col gap-2">
              <label htmlFor="quiz_id">إختر الإمتحان (اختياري)</label>
              {isSubscribed() ? (
                <select
                  onChange={(e) =>
                    setLesson((prev) => ({
                      ...prev,
                      quiz_id: e.target.value,
                    }))
                  }
                  value={lesson.quiz_id}
                  id="quiz_id"
                  name="quiz_id"
                  className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) mb-3"
                  disabled={!isSubscribed()}
                >
                  <option selected value=""></option>
                  {userProfileData?.hws_quizzes_lessons
                    ?.filter((item) => item.post_type === "quiz")
                    ?.map((quiz) => (
                      <option value={quiz.ID} key={quiz.ID}>
                        {convertText(quiz.post_title)}
                      </option>
                    ))}
                </select>
              ) : (
                <div
                  onClick={() => dispatch(pushModal(<SubscriptionCTA />))}
                  className="min-h-10 block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) mb-3"
                ></div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="hw_id">إختر الواجب (اختياري)</label>
              {isSubscribed() ? (
                <select
                  onChange={(e) =>
                    setLesson((prev) => ({
                      ...prev,
                      hw_id: e.target.value,
                    }))
                  }
                  value={lesson.hw_id}
                  id="hw_id"
                  name="hw_id"
                  className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) mb-3"
                  disabled={!isSubscribed()}
                >
                  <option selected value=""></option>
                  {userProfileData?.hws_quizzes_lessons
                    ?.filter((item) => item.post_type === "homework")
                    ?.map((homework) => (
                      <option value={homework.ID} key={homework.ID}>
                        {convertText(homework.post_title)}
                      </option>
                    ))}
                </select>
              ) : (
                <div
                  onClick={() => dispatch(pushModal(<SubscriptionCTA />))}
                  className="min-h-10 block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) mb-3"
                ></div>
              )}
            </div>
          </div>
          <Separator />
          <h3 className="text-xl font-bold border-b-2 border-(--primary-color-light) pb-2 w-fit">
            الفيديوهات
          </h3>
          {lesson.videos?.map((question, index) => (
            <div
              key={question.video_id}
              className="flex flex-col md:flex-row gap-2 p-4 bg-indigo-100 rounded-xl"
            >
              <div className="space-y-2 grow md:max-w-1/2">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor={`vid_quiz_title_${index}`}
                    className="text-base"
                  >
                    عنوان الفيديو
                  </label>
                  <input
                    type="text"
                    className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
                    id={`vid_quiz_title_${index}`}
                    name={`vid_quiz_title_${index}`}
                    placeholder=" "
                    required
                    onChange={(e) =>
                      setLesson((prev) => ({
                        ...prev,
                        videos: prev.videos.map((item, i) => {
                          if (i === index) {
                            return {
                              ...item,
                              video_title: e.target.value,
                            };
                          }
                          return item;
                        }),
                      }))
                    }
                    value={lesson.videos[index].video_title}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor={`vid_url_${index}`} className="text-base">
                    لينك الفيديو
                  </label>
                  <input
                    type="text"
                    className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
                    id={`vid_url_${index}`}
                    name={`vid_url_${index}`}
                    placeholder=""
                    required
                    value={lesson.videos[index].video_url}
                    onChange={(e) => {
                      setLesson((prev) => ({
                        ...prev,
                        videos: prev.videos.map((item, i) => {
                          if (i === index) {
                            return {
                              ...item,
                              video_url: getVideoInfo(e.target.value).href,
                              video_id: getVideoInfo(e.target.value).id,
                              video_host: getVideoInfo(e.target.value).host as
                                | "youtube"
                                | "vimeo",
                            };
                          }
                          return item;
                        }),
                      }));
                    }}
                  />
                </div>
                {["localhost", "mohamedgalal.com"].includes(getHostname()) && (
                  <div id={`vid_url_${index}_2_preview_div`}>
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor={`vid_url_${index}_2`}
                        className="text-base"
                      >
                        لينك الفيديو القديم
                      </label>
                      <input
                        type="text"
                        className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
                        id={`vid_url_${index}_2`}
                        name={`vid_url_${index}_2`}
                        placeholder=" "
                        value={lesson.videos[index].video_url2}
                        onChange={(e) => {
                          setLesson((prev) => ({
                            ...prev,
                            videos: prev.videos.map((item, i) => {
                              if (i === index) {
                                return {
                                  ...item,
                                  video_url2: getVideoInfo(e.target.value).href,
                                  video_id2: getVideoInfo(e.target.value).id,
                                  video_host: getVideoInfo(e.target.value)
                                    .host as "youtube" | "vimeo",
                                };
                              }
                              return item;
                            }),
                          }));
                        }}
                      />
                    </div>
                  </div>
                )}
                {lesson.videos.length > 1 && (
                  <Button
                    type="button"
                    variant="link"
                    className="text-white bg-red-500"
                    onClick={() => {
                      setLesson((prev) => ({
                        ...prev,
                        videos: prev.videos.filter((_, idx) => idx !== index),
                      }));
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
              <div
                id={`vid_url_${index}_preview_div`}
                className="grow min-w-1/2"
              >
                {!!lesson.videos[index].video_url && (
                  <iframe
                    className="w-full h-full min-h-[260px] rounded-lg"
                    src={`https://www.youtube.com/embed/${extractVideoId(
                      lesson.videos[index].video_url
                    )}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
                    allowFullScreen
                  ></iframe>
                )}
              </div>
            </div>
          ))}
          {lesson.video_on_off && (
            <div id="ls_add_vid_url" className="my-3">
              <Button
                type="button"
                onClick={() =>
                  setLesson((prev) => ({
                    ...prev,
                    videos: [
                      ...prev.videos,
                      {
                        video_title: "",
                        video_id: "",
                        video_url: "",
                        video_id2: "",
                        video_url2: "",
                        video_host: "youtube",
                      },
                    ],
                  }))
                }
                id="ls_add_vid_url_btn"
                className="text-white text-2xl bg-green-600 hover:bg-green-700"
              >
                +
              </Button>
            </div>
          )}
          <div className="space-y-2 hidden">
            <input
              type="text"
              className="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) peer"
              id="comments_or_hw"
              name="comments_or_hw"
              placeholder=" "
            />
            <label htmlFor="comments_or_hw" className="text-base">
              تعليقات أو الواجب
            </label>
          </div>
          <Separator />
          <h3 className="text-xl font-bold border-b-2 border-(--primary-color-light) pb-2 w-fit">
            الملفات
          </h3>
          {fileProgress && fileProgress < 100 && fileProgress >= 0 && (
            <FileUploadProgressBar fileProgress={fileProgress} />
          )}

          <>
            <input
              style={{ textAlign: "center" }}
              id={`lesson_file_input`}
              onChange={(e) => handleFileUploadFb(e)}
              className="elite-file lesson_form_input w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-[7px] appearance-none focus:outline-none focus:ring-2 focus:ring-(--primary-color-light) focus:border-(--primary-color-light) mt-2 text-center"
              type="file"
              hidden
            />
            <Button
              className="text-primary w-full bg-purple-100 hover:bg-purple-200 flex items-center justify-center border-dashed border-2 border-purple-400 gap-2 p-8 h-fit rounded-lg text-lg"
              type="button"
              onClick={() =>
                isSubscribed()
                  ? document.getElementById(`lesson_file_input`)?.click()
                  : dispatch(pushModal(<SubscriptionCTA />))
              }
            >
              <Upload className="size-5" />
              رفع ملف
            </Button>
          </>

          {lesson.lessonFiles?.map((file, index) => (
            <div key={file.name} className="flex flex-col gap-2">
              <div className="flex gap-2 items-center justify-between bg-purple-100 p-2 rounded-lg">
                <span className="flex gap-2 items-center">
                  <FileText className="size-7 text-purple-700" />
                  <span>{file.name}</span>
                </span>
                <Button
                  type="button"
                  disabled={
                    typeof fileProgress === "number" && !isNaN(fileProgress)
                  }
                  onClick={() => {
                    setLesson((prev) => ({
                      ...prev,
                      lessonFiles: prev.lessonFiles.filter(
                        (_, i) => i !== index
                      ),
                    }));
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ))}

          <Button
            type="submit"
            disabled={
              publishing ||
              (fileProgress && fileProgress < 100 && fileProgress >= 0)
                ? true
                : false
            }
            id="publish_new_quiz_submit"
            className="w-full my-4"
          >
            {publishing ? <Spinner variant="button" /> : "نشر الحصة"}
          </Button>

          {successMsg && (
            <div className="p-3 bg-green-100 text-green-800 border border-green-400 rounded-[7px] h-fit mt-2">
              تم النشر{" "}
              <Link
                to={`/category/${successMsg.teacher_slug}/${successMsg.lesson_slug}`}
                className="text-green-800 underline"
              >
                إضغط هنا للذهاب للحصة
              </Link>
            </div>
          )}
        </form>
      </>
    </Animation>
  );
};

export default CreateLesson;
